import apiClient from './apiClient';

// Define interfaces for type safety
interface Organization {
  name: string;
  [key: string]: any;
}

interface IdealCandidate {
  id: string;
  [key: string]: any;
}

interface SkillCriterion {
    id: string;
    level: { id: string };
    notes: string;
}

interface BehaviorCriterion {
    id: string;
    level: { id: string };
    notes: string;
}

interface Results {
  name: string;
  email: string;
  phone: string;
  race: string;
  gender: string;
  skills: { [key: string]: SkillCriterion };
  behaviors: { [key: string]: BehaviorCriterion };
}

interface ReportData {
  job_title: string;
  department: string;
  posting_date: string;
  report_start_date: string;
  report_end_date: string;
  total_applicants: number;
  avg_applications_per_day: number;
  screened_count: number;
  interviewed_count: number;
  offer_count: number;
  hired_count: number;
  male_percent: number;
  female_percent: number;
  other_percent: number;
  undisclosed_percent: number;
  top_locations: Array<{ location: string; count: number }>;
  sources: Array<{ source: string; applicants: number; percent: number }>;
  avg_qualification_score: number;
  avg_skills_match_percent: number;
  avg_experience: number;
  top_hours: string;
  top_day: string;
  avg_time_to_apply: number;
  screening_pass_rate: number;
}

export async function listIdealCandidates(organization: Organization, includeArchived: boolean): Promise<any> {
  const parameters = new URLSearchParams({
    includeArchived: includeArchived ? "true" : "false",
  });
  const endpoint = `organizations/${organization.name}/ideal-candidates?${parameters}`;
  return apiClient.get(endpoint);
}

export async function createIdealCandidate(organization: Organization, idealCandidate: IdealCandidate): Promise<any> {
  const endpoint = `organizations/${organization.name}/ideal-candidates`;
  return apiClient.post(endpoint, idealCandidate);
}

export async function getIdealCandidate(organization: Organization, idealCandidateUuid: string): Promise<any> {
  const endpoint = `organizations/${organization.name}/ideal-candidates/${idealCandidateUuid}`;
  return apiClient.get(endpoint);
}

export async function listMatches(organization: Organization, idealCandidateUuid: string): Promise<any> {
  const endpoint = `organizations/${organization.name}/ideal-candidates/${idealCandidateUuid}/matches`;
  return apiClient.get(endpoint);
}

export async function getReport(organization: Organization, idealCandidateUuid: string): Promise<ReportData> {
  const endpoint = `organizations/${organization.name}/ideal-candidates/${idealCandidateUuid}/matches?report=true`;
  return apiClient.get(endpoint);
}

export async function getIdealCandidatePublic(idealCandidateUuid: string): Promise<any> {
  const endpoint = `ideal-candidates/${idealCandidateUuid}`;
  return apiClient.get(endpoint, true); // This is a public endpoint
}

export async function createMatchPublic(idealCandidate: IdealCandidate, results: Results): Promise<any> {
  const endpoint = `ideal-candidates/${idealCandidate.id}/matches`;
  const body = {
    candidate: {
      name: results.name,
      email: results.email,
      phone: results.phone,
      race: results.race,
      gender: results.gender,
    },
    scorecard_results: [
      {
        scorecard_name: "skills",
        version: "1",
        criteria: Object.values(results.skills).map((skill: SkillCriterion) => ({
          id: skill.id,
          value: skill.level.id,
          notes: skill.notes,
        })),
      },
      {
        scorecard_name: "behaviors",
        version: "1",
        criteria: Object.values(results.behaviors).map((behavior: BehaviorCriterion) => ({
          id: behavior.id,
          value: behavior.level.id,
          notes: behavior.notes,
        })),
      },
    ],
  };
  return apiClient.post(endpoint, body, true); 
}

export async function updateMatchStatus(organization: Organization, idealCandidateUuid: string, matchUuid: string, status: string): Promise<any> {
  const endpoint = `organizations/${organization.name}/ideal-candidates/${idealCandidateUuid}/matches/${matchUuid}`;
  return apiClient.patch(endpoint, { status });
}

export async function archiveIdealCandidate(organization: Organization, idealCandidateUuid: string): Promise<any> {
  const endpoint = `organizations/${organization.name}/ideal-candidates/${idealCandidateUuid}/archive`;
  return apiClient.post(endpoint, {});
}

export async function unarchiveIdealCandidate(organization: Organization, idealCandidateUuid: string): Promise<any> {
  const endpoint = `organizations/${organization.name}/ideal-candidates/${idealCandidateUuid}/unarchive`;
  return apiClient.post(endpoint, {});
}